
import React from 'react';
import { 
  Bot, 
  Brain, 
  Users, 
  Phone, 
  Mail, 
  Workflow, 
  Megaphone, 
  Settings, 
  BarChart3 
} from 'lucide-react';

export function FeaturesSection() {
  const features = [
    {
      icon: Bot,
      title: "AI Agents That Work Like Your Best Reps — and Never Sleep",
      description: "Deploy always-on, intelligent sales agents to qualify leads, follow up automatically, and handle outreach. Each agent is fully configurable to match your tone, style, and workflow—so it feels like your best rep, just faster."
    },
    {
      icon: Brain,
      title: "Smart Lead Generation & Predictive Scoring",
      description: "Combine AI discovery with manual imports to build enriched, prioritized lead lists. Our scoring engine analyzes behavior, fit, and timing—so you focus only on the leads most likely to convert."
    },
    {
      icon: Users,
      title: "Hybrid Mode: AI + Human Salesforce, in Sync",
      description: "Let AI handle the repetitive and real-time grunt work while your team steps in when it matters most. Assign tasks between agents and humans, hand off conversations, and track everything in one unified interface."
    },
    {
      icon: Phone,
      title: "Built-in Calling & IVR, Ready from Day One",
      description: "Launch outbound call campaigns, configure inbound IVR menus, drop voicemails, and record call outcomes—all without external tools. Fully integrated with your workflows and analytics."
    },
    {
      icon: Mail,
      title: "AI-Powered Email & SMS Sequences",
      description: "Send laser-focused emails and texts with perfect timing. Dynamic content, smart delays, and automated follow-ups ensure no lead falls through the cracks—while still sounding human."
    },
    {
      icon: Workflow,
      title: "Visual Workflows that Orchestrate Everything",
      description: "Design advanced sales journeys with an intuitive drag-and-drop builder. Trigger agents, send messages, launch calls, assign reps, or move deals—automatically and on your terms."
    },
    {
      icon: Megaphone,
      title: "Multi-Channel Campaigns, Fully Synced",
      description: "Run seamless campaigns across voice, SMS, and email. Schedule, segment, test, and iterate—all from a single dashboard with real-time feedback loops."
    },
    {
      icon: Settings,
      title: "Configurable AI Agent Settings",
      description: "Customize every aspect of your AI agents—from tone and persona to fallback logic, response behavior, and campaign goals. Build outreach agents, support agents, nurturing flows—all without a single line of code."
    },
    {
      icon: BarChart3,
      title: "Real-Time Sales Intelligence & Agent Analytics",
      description: "Get a clear view of what's working. Track conversion rates, agent performance, campaign ROI, and human + AI collaboration effectiveness. Optimize every stage with confidence."
    }
  ];

  return (
    <div className="py-20 px-4 bg-background">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
            Features Built for <span className="text-yellow-400">Growth</span> — Fueled by AI Agents
          </h2>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
            Work smarter with AI-powered workflows, hands-free campaigns, and hybrid agents that collaborate seamlessly with your human team.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="group p-6 bg-card border border-border rounded-2xl shadow-sm hover:shadow-xl hover:bg-accent/50 transition-all duration-300">
              <div className="flex items-start gap-x-4 mb-4">
                <div className="inline-flex justify-center items-center w-12 h-12 text-yellow-400 bg-yellow-400/10 rounded-xl border border-yellow-400/20 group-hover:bg-yellow-400/20 transition-colors flex-shrink-0 mt-1">
                  <feature.icon className="w-6 h-6"/>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-3 leading-tight">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
